import { ROLE_JDTECH_ADMIN, ROLE_SYSTEM_ADMIN } from "./constants";

export const ALL_LOCATIONS = {
  location_id: -1,
  location_name: "All Locations",
};

function parseAssignedLocations(raw) {
  let list = raw;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) {
    return [];
  }
  return list.filter((location) => location?.location_id != null);
}

/**
 * Whether a stylist works at the given location: any assigned location
 * (`location_ids`), falling back to the primary `location_id`.
 */
export function worksAtLocation(stylist, locationId) {
  if (locationId == null) return false;
  const target = Number(locationId);
  let ids = stylist?.location_ids;
  if (typeof ids === "string") {
    try {
      ids = JSON.parse(ids);
    } catch {
      ids = null;
    }
  }
  if (Array.isArray(ids) && ids.length > 0) {
    return ids.some((id) => Number(id) === target);
  }
  return stylist?.location_id != null && Number(stylist.location_id) === target;
}

export function canViewAllLocations(user) {
  return (
    user?.userRoles?.includes(ROLE_SYSTEM_ADMIN) ||
    user?.userRoles?.includes(ROLE_JDTECH_ADMIN)
  );
}

/**
 * Locations the signed-in user is assigned to.
 * Falls back to the user's primary location when the assignment list is empty.
 */
export function getAssignedLocations(user) {
  const assigned = parseAssignedLocations(user?.assignedLocations);
  if (assigned.length > 0) {
    return assigned;
  }
  if (user?.location_id) {
    return [
      {
        location_id: user.location_id,
        location_name: user.location_name || "",
      },
    ];
  }
  return [];
}

/**
 * Default scheduler/header location after login.
 * Org-wide admin access → All Locations.
 * Otherwise → the home (primary) location, or the first assigned one.
 * Non-admins never default to All Locations: it isn't in their selector.
 */
export function getDefaultLocationForUser(user) {
  if (canViewAllLocations(user)) {
    return ALL_LOCATIONS;
  }
  const assigned = getAssignedLocations(user);
  if (assigned.length === 0) {
    return ALL_LOCATIONS;
  }
  const home =
    assigned.find((loc) => loc.location_id === user?.location_id) ??
    assigned[0];
  return {
    location_id: home.location_id,
    location_name: home.location_name || "",
  };
}

export function persistSelectedLocation(location) {
  if (!location) return;
  try {
    sessionStorage.setItem("selectedLocationId", String(location.location_id));
    sessionStorage.setItem(
      "selectedLocationName",
      location.location_name || ""
    );
  } catch {
    /* sessionStorage unavailable */
  }
}

/**
 * Keep an explicit in-session choice (including All Locations for admins).
 * Otherwise apply the post-login default for this user.
 */
export function resolveInitialLocation(user) {
  try {
    const storedId = sessionStorage.getItem("selectedLocationId");
    const storedName = sessionStorage.getItem("selectedLocationName");
    const parsed = parseInt(storedId, 10);
    if (storedId != null && storedId !== "" && !Number.isNaN(parsed)) {
      const assigned = getAssignedLocations(user);
      const selectable =
        canViewAllLocations(user) ||
        assigned.length === 0 ||
        assigned.some((loc) => Number(loc.location_id) === parsed);
      if (selectable) {
        return { location_id: parsed, location_name: storedName || null };
      }
    }
  } catch {
    /* sessionStorage unavailable */
  }
  return getDefaultLocationForUser(user);
}

export function applyUserLocation(user, setLocation) {
  const next = resolveInitialLocation(user);
  persistSelectedLocation(next);
  if (typeof setLocation === "function") {
    setLocation(next);
  }
  return next;
}
